// ConsentFormModal.tsx
import { Form, Input, Modal } from 'antd';
import Box from 'components/Box';
import Checkbox from 'components/Checkbox';
import InputDOBV2 from 'components/InputDOBV2';
import cashierSelectors from 'features/cashier/services/selectors';
import customerApis from 'features/customer/services/apis';
import { ICustomer } from 'features/customer/services/types/api';
import { SocketConsentData, TypeSocketConsentActors, TypeSocketConsentPassData } from 'features/payment/pages/PaymentPage/services/types/socketConsent';
import paymentApis from 'features/payment/services/apis';
import { IBillDetailData } from 'features/payment/services/types/bill';
import { IBodyApiSubmitConsentForm } from 'features/payment/services/types/ticket';
import { useSocketContext } from 'hooks/useSocket';
import { debounce } from 'lodash';
import moment from 'moment';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import { IResponseDataBody } from 'services/response';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { Message } from 'stompjs';
import styled from 'styled-components';
import { removeBase64Prefix } from 'utils/removeBase64Prefix';
import storage from 'utils/sessionStorage';
import ic_clear from './ic-delete.svg';
import ModalWarningConsentForm from './ModalWarningConsent';
import { formatPhone } from 'utils/formatPhone';
import HTMLEditor from 'features/settingService/pages/SettingPage/components/PaymentTabMenu/ConsentForm/HtmlEditor';
import shopSelectors from 'services/shop/selectors';
const SIGNATURE_WIDTH = 650 * 1.7;
const SIGNATURE_HEIGHT = 250 * 1.7;
const ZOOM_SIZE = 1.2;

export type ConsentFormValues = {
  customerId: string;
  billId: string;
  fullName: string;
  phoneNumber?: string;
  dob?: string | null;
  email?: string;
  agreement?: string;
  allergies?: string;
  medicalConditions?: string;
  consent: boolean;
  signature?: string | null; // dataURL (base64)
};

export type ConsentFormRef = {
  open: (customerId: string) => Promise<boolean>;
  openNonConfirm: (customerId: string) => Promise<void>;
  close: () => void;
  submit: () => void;
  resetSignature: () => void;
  retrieveCustomerId: (customerId: string) => Promise<void>;
};

export type ConsentFormProps = {
  title?: string;
  destroyOnClose?: boolean;
  initialValues?: Partial<ConsentFormValues>;
  onSubmit?: (values: ConsentFormValues) => void | Promise<void>;
};

const DEFAULTS: ConsentFormValues = {
  customerId: '',
  billId: '',
  fullName: '',
  phoneNumber: '',
  dob: null,
  email: '',
  allergies: '',
  consent: true,
  medicalConditions: '',
  signature: null,
};

const rules = {
  fullName: [{ required: true, message: 'Please enter full name' }],
  phoneNumber: [
    { pattern: /^\+?[0-9\s()-]{6,}$/i, message: 'Phone looks invalid' },
  ],
  email: [{ type: 'email' as const, message: 'Email is invalid' }],
};
export const consentFormRef = React.createRef<ConsentFormRef>();
const ModalConsentForm = forwardRef<ConsentFormRef, ConsentFormProps>(
  function ModalConsentForm(
    { title = 'Client Intake Form', destroyOnClose = false, initialValues, onSubmit },
    ref,
  ) {
    const socketContext = useSocketContext();
    const [openWarning, setOpenWarning] = useState(false);
    const setLoadingPage = useSetLoadingPage();
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm<ConsentFormValues>();
    const callbackResolve = useRef<(value: boolean) => void>(() => undefined);
    const [customerId, setCustomerId] = useState<string>('');
    const consentFormMsg = cashierSelectors.getConsentFormMsg();
    const agreementData = shopSelectors.data.agreementData();

    useEffect(() => {
      if (!consentFormMsg) return;
      listeningData(consentFormMsg);
    }, [consentFormMsg]);

    const sendData = (action: TypeSocketConsentPassData, data: any = {}) => {
      if (!customerId) return;
      socketContext.sendConsentForm(customerId, {
        actor: TypeSocketConsentActors.CASHIER,
        action,
        data,
      });
    };

    const listeningData = (message: Message['body']) => {
      if (!message) return;
      const payment: SocketConsentData = JSON.parse(message);
      if (!payment) return;
      if (payment.shopId !== storage.shop_id.get()) return;

      if (payment.actor !== TypeSocketConsentActors.CUSTOMER) return;

      switch (payment.action) {
        case TypeSocketConsentPassData.SEND_SIGNATURE: {
          const signature = payment.data || '';
          form.setFieldValue('signature', signature);
          signRef.current?.clear();
          setTimeout(() => {
            signRef.current?.fromDataURL(signature, { height: SIGNATURE_HEIGHT, width: SIGNATURE_WIDTH });
          }, 100);
          break;
        }
        case TypeSocketConsentPassData.SEND_CONSENT_FORM_DATA: {
          const data = {
            ...payment.data,
            phoneNumber: formatPhone(payment.data.phoneNumber),
          };
          form.setFieldsValue(data);
          break;
        }
        case TypeSocketConsentPassData.PROGRESSING: {
          setLoadingPage(true);
          break;
        }
        case TypeSocketConsentPassData.PROGRESSED: {
          setLoadingPage(false);
          break;
        }
        case TypeSocketConsentPassData.COMPLETE_CONSENT: {
          setOpen(false);
          setLoadingPage(false);
          callbackResolve.current(true);
          break;
        }
      }
    };

    const signRef = useRef<ReactSignatureCanvas>(null);

    const handleOpen: ConsentFormRef['open'] = async (_customerId) => {
      setCustomerId(_customerId);
      return new Promise<boolean>((res) => {
        setOpenWarning(true);
        callbackResolve.current = res;
      });
    };

    const handleClose = () => {
      sendData(TypeSocketConsentPassData.CANCEL_CONSENT);
      setOpen(false);
    };

    const handleFinish = async (data: any) => {
      const values = data as ConsentFormValues;
      const signature = values.signature ?? '';
      const payload: IBodyApiSubmitConsentForm = {
        customerId: values.customerId || '',
        name: values.fullName || '',
        phoneNumber: (values.phoneNumber || '')?.replace(/[^0-9]/g, '') || '',
        dob: values.dob || '',
        email: values.email || '',
        allergiesOrSensitivities: values.allergies || '',
        medicalConditions: values.medicalConditions || '',
        consent: values.consent || false,
        base64Signature: removeBase64Prefix(signature),
      };
      sendData(TypeSocketConsentPassData.PROGRESSING);
      setLoadingPage(true);
      try {
        const res: IResponseDataBody<any> = await paymentApis.submitConsentForm(payload);
        if (res.data.data) {
          callbackResolve.current(true);
          sendData(TypeSocketConsentPassData.COMPLETE_CONSENT, {});
          setOpen(false);
        } else {
          throw 'fail';
        }
      } catch (e) {
        sendData(TypeSocketConsentPassData.PROGRESSED);
      } finally {
        setLoadingPage(false);
      }
    };

    const submit = () => form.submit();

    const resetSignature = () => {
      signRef.current?.clear();
      form.setFieldValue('signature', null);
    };


    const getCustomerDetail = async (customerId: string) => {
      try {
        const res: IResponseDataBody<ICustomer> = await customerApis.getCustomerDetails(customerId);
        const resData = res?.data.data;
        if (resData) {
          form.setFieldsValue({
            customerId: resData?.id || '',
            fullName: resData?.name || '',
            phoneNumber: resData?.phone || '',
            dob: moment(resData?.birthday, 'YYYY-MM-DD').format('MM/DD') || '',
            email: resData?.email || '',
          });
        }
      } catch (error) {

      }
    };

    const onSocketValueChange = debounce((field: keyof ConsentFormValues, data: any) => {
      const values = form.getFieldsValue();
      const newValues = { ...values, [field]: data };
      delete newValues.signature;
      sendData(TypeSocketConsentPassData.SEND_CONSENT_FORM_DATA, newValues);
    }, 200);

    const onSignatureChange = debounce((_value: string) => {
      undefined;
    }, 200);

    const onRequestConsent = async (_customerId?: string) => {
      const cusId = _customerId || customerId;
      form.resetFields();
      form.setFieldValue('agreement', agreementData?.agreement || '');
      setOpenWarning(false);
      socketContext.sendConsentForm(cusId, {
        actor: TypeSocketConsentActors.CASHIER,
        action: TypeSocketConsentPassData.REQUEST_CONSENT,
        data: {},
      });

      await getCustomerDetail(cusId);
      setOpen(true);
    };

    const onCancelConsent = () => {
      setOpenWarning(false);
      callbackResolve.current(true);
    };

    useImperativeHandle(
      ref,
      () => ({
        open: handleOpen, close: handleClose, submit, resetSignature,
        openNonConfirm: async (_customerId) => {
          setCustomerId(_customerId);
          return await onRequestConsent(_customerId);
        },
        retrieveCustomerId: async (customerId) => {
          form.resetFields();
          setOpenWarning(false);
          await getCustomerDetail(customerId);
          setOpen(true);
        },
      }),
    );

    return (
      <>
        <ModalWarningConsentForm component onSubmit={onRequestConsent} visible={openWarning} onClose={onCancelConsent} onCancel={onCancelConsent} />
        {!!open && <Modal
          open={open}
          onCancel={handleClose}
          destroyOnClose={destroyOnClose}
          footer={null}
          width={'1600px'}
          closable={false}
          wrapProps={{ style: { zIndex: 9999 } }}
          bodyStyle={{ padding: 0, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <FormStyled
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={{ ...DEFAULTS, ...(initialValues || {}) }}
          >
            <Form.Item name={'customerId'} noStyle />
            <Form.Item name={'billId'} noStyle />
            <Form.Item name="signature" noStyle rules={[{ required: true }]} />
            <Form.Item name="consent" noStyle rules={[{
              validator: (_, value) => {
                if (!value) return Promise.reject('Please accept');
                return Promise.resolve();
              },
            }]} />
            <Form.Item name="fullName" noStyle rules={[{ required: true }]} />
            <Form.Item name="phoneNumber" noStyle rules={[{ required: true }]} />
            <Form.Item name="dob" noStyle rules={[{ required: true }]} />

            <Box display='flex' alignItems='center' alignSelf='stretch'>
              <Box display='flex' flexDirection='column' className='form-content'>
                <p className="title-consent">Client Information & Consent Form</p>
                <div className="date-group">
                  <span>Date: <span className='value'>{moment().format('MM-DD-YYYY hh:mm A')}</span></span>
                </div>
                <Box display='flex' flexDirection='column'>
                  <Form.Item
                    className="full"
                    name="fullName"
                    label="Full name"
                    rules={rules.fullName}
                  >
                    <Input allowClear placeholder="" onChange={e => onSocketValueChange('fullName', e.target.value)} />
                  </Form.Item>

                  <Form.Item
                    name="phoneNumber"
                    label="Phone"
                    rules={rules.phoneNumber}
                  >
                    <Input allowClear placeholder="" onChange={e => onSocketValueChange('phoneNumber', e.target.value)} />
                  </Form.Item>

                  <Form.Item name="dob" label="Date of birth">
                    <InputDOBV2 style={{ width: '100%' }} onChange={(_e, dataStr) => onSocketValueChange('dob', dataStr)} />
                  </Form.Item>

                  <Form.Item name="email" label="Email" rules={rules.email}>
                    <Input allowClear type="email" placeholder="Please enter..." onChange={e => onSocketValueChange('email', e.target.value)} />
                  </Form.Item>

                  <Form.Item className="full" name="allergies" label="Allergies">
                    <Input.TextArea rows={3} placeholder="Please list any allergies or sensitivities we should be aware of..." onChange={e => onSocketValueChange('allergies', e.target.value)} />
                  </Form.Item>

                  <Form.Item
                    className="full"
                    name="medicalConditions"
                    label="Medical conditions"
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder="Any medical conditions that might affect nail services..."
                      onChange={e => onSocketValueChange('medicalConditions', e.target.value)}
                    />
                  </Form.Item>
                </Box>
              </Box>
              <Box display='flex' flexDirection='column' alignSelf='stretch' style={{ width: SIGNATURE_WIDTH + 8 }}>
                <Box p='4' display='flex' flexDirection='column' alignSelf='stretch' style={{ zoom: ZOOM_SIZE }}>
                  <p className="consent-accept-title">Consent & Agreement</p>
                  <Box display='flex' flexDirection='column' className='consent-content' py='4'>
                    <HTMLEditor readOnly value={agreementData?.agreement} />
                  </Box>
                  <Form.Item className="full" name="consent" noStyle>
                    <Checkbox round onChange={e => onSocketValueChange('consent', e)} label={agreementData?.confirmInfo || ''} />
                  </Form.Item>
                </Box>
                <Box px='1' display='flex' flexDirection='column' alignItems='center' alignSelf='stretch' style={{ flex: 1, justifyContent: 'flex-end' }}>
                  <Form.Item noStyle name={'signature'}>
                    <SignatureConsent signRef={signRef} onChange={onSignatureChange} />
                  </Form.Item>
                </Box>
                <Box style={{ zoom: ZOOM_SIZE }} px='4' py='2' display='flex' alignItems='center' gap='1' justifyContent='space-between' className='signature-footer'>
                  <button type='button' style={{ display: 'flex', alignItems: 'center', gap: '4px', }} onClick={() => {
                    form.setFieldValue('signature', '');
                    signRef.current?.clear();
                  }}>
                    <img src={ic_clear} alt='ic_clear' style={{ width: 24, height: 24 }} /> <span className='clear-text'>Clear</span>
                  </button>

                  <span className='sign-above'>Sign above</span>
                </Box>
                <Box style={{ zoom: ZOOM_SIZE }} py='2' gap='3' display='flex' flexDirection='column' alignSelf='stretch' className='footer-action'>
                  <Box gap='4' px='4' display='flex' alignItems='center' alignSelf='stretch'>
                    <button onClick={handleClose} type='button' className='btn btn-cancel'><span>Cancel</span></button>
                    <Form.Item shouldUpdate noStyle dependencies={['consent', 'signature']}>{({ getFieldValue }) => {
                      const acceptConsent = getFieldValue('consent');
                      const signature = getFieldValue('signature');
                      return <button disabled={!acceptConsent || !signature} type='submit' className='btn btn-submit'><span>Submit</span></button>;
                    }}</Form.Item>
                  </Box>
                  <p className="footer-action-text">Your information is secure and will only be used for service purposes.</p>
                </Box>
              </Box>
            </Box>
            <Box style={{ zoom: ZOOM_SIZE }} className='footer-consent' display='flex' alignItems='center' gap='2' justifyContent='center'>
              <span>Powered by</span>
              <FozitoIcon />
            </Box>
          </FormStyled>
        </Modal>}
      </>
    );
  },
);

ModalConsentForm.displayName = 'ModalConsentForm';

export default ModalConsentForm;
type SignatureProps = {
  signRef: React.RefObject<ReactSignatureCanvas>;
  onChange?: (value: string) => void;
};
const SignatureConsent = ({ signRef, onChange }: SignatureProps) => {
  return (
    <>
      <ReactSignatureCanvas ref={signRef}
        canvasProps={{
          width: SIGNATURE_WIDTH,
          height: SIGNATURE_HEIGHT,
          className: 'sigCanvas',
        }}
        minWidth={3}
        maxWidth={3}
        onEnd={(event: MouseEvent) => {
          const element = event?.target as HTMLCanvasElement;
          if (!element?.toDataURL) return;
          const dataURL = element?.toDataURL() || '';
          onChange?.(dataURL);
        }}
      />
    </>

  );
};

const FozitoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="62" height="17" viewBox="0 0 62 17" fill="none">
  <path d="M37.863 2.19878C38.1144 2.17048 38.3713 2.17081 38.6221 2.2081C38.9681 2.28732 39.3125 2.46707 39.5004 2.77531C39.69 3.06157 39.7065 3.43838 39.5791 3.75195C39.4615 3.99827 39.2638 4.21197 39.0127 4.32881C38.6819 4.5049 38.2936 4.51788 37.9276 4.48592C37.5207 4.43533 37.1138 4.22595 36.9029 3.86945C36.6886 3.51361 36.7194 3.03794 36.9654 2.70474C37.177 2.42413 37.518 2.25669 37.863 2.19878Z" fill="#FF8890" />
  <path d="M37.0859 5.67536C37.8328 5.6737 38.5794 5.67004 39.3262 5.6767C39.3303 8.58498 39.3303 11.4933 39.3262 14.4016C38.5797 14.3969 37.8328 14.4006 37.0863 14.3992C37.094 12.3894 37.0876 10.3791 37.0897 8.36895C37.0873 7.47086 37.0944 6.57311 37.0859 5.67536Z" fill="#FF8890" />
  <path d="M1.61948 2.85449C5.11324 2.85716 8.60734 2.85416 12.1011 2.85582C12.0991 3.54154 12.1011 4.22691 12.1001 4.91263C9.40051 4.91163 6.70128 4.91196 4.00205 4.91229C4.00239 5.91723 4.00171 6.92183 4.00239 7.92643C6.29439 7.9261 8.58638 7.92577 10.8784 7.92676C10.8787 8.5702 10.8774 9.21364 10.8791 9.85741C8.58672 9.85874 6.29438 9.85808 4.00205 9.85741C4.00272 11.3686 4.00137 12.8795 4.00272 14.3908C3.2082 14.3934 2.41367 14.3911 1.61914 14.3918C1.62015 10.5461 1.61948 6.70047 1.61948 2.85449Z" fill="#1D2129" />
  <path d="M43.4876 2.97541C44.2284 2.97441 44.9688 2.97441 45.7096 2.97507C45.7106 3.87715 45.7106 4.77923 45.7096 5.68131C46.4223 5.68031 47.1347 5.68164 47.8475 5.68064C47.8455 6.22888 47.8461 6.77745 47.8468 7.32568C47.1344 7.32369 46.422 7.32469 45.7096 7.32502C45.7113 9.68041 45.7099 12.0358 45.7103 14.3912C44.9691 14.3945 44.228 14.3942 43.4872 14.3912C43.4889 12.0355 43.4876 9.67974 43.4876 7.32402C42.7752 7.32535 42.0631 7.32402 41.3507 7.32468C41.3524 6.77678 41.353 6.22854 41.3503 5.68064C42.0624 5.68164 42.7745 5.67997 43.4869 5.68164C43.4889 4.77956 43.4876 3.87748 43.4876 2.97541Z" fill="#1D2129" />
  <path d="M26.5477 5.68056C29.4331 5.68022 32.3182 5.67523 35.2037 5.67723C33.3872 7.97669 31.5693 10.2748 29.7488 12.571C31.4663 12.5683 33.1841 12.5763 34.9016 12.572C34.8995 13.1791 34.9029 13.7866 34.9002 14.3938C31.8208 14.3918 28.7417 14.3941 25.6626 14.3928C27.4632 12.0926 29.273 9.79916 31.0769 7.50135C29.5673 7.49969 28.058 7.50335 26.5484 7.49936C26.5501 6.8932 26.5514 6.28705 26.5477 5.68056Z" fill="#1D2129" />
  <path d="M18.7536 5.51871C19.0801 5.48809 19.4066 5.52637 19.7334 5.52637C20.2481 5.58062 20.7678 5.65019 21.2565 5.82562C21.635 5.94046 21.9909 6.11388 22.341 6.2943C22.563 6.43577 22.7983 6.56159 22.9895 6.74467C23.1119 6.81291 23.1937 6.93108 23.3126 7.00497C23.5769 7.28359 23.8314 7.57651 24.0257 7.90838C24.3539 8.45429 24.5475 9.08075 24.5901 9.71387C24.6263 9.95021 24.6188 10.1932 24.582 10.4289C24.5833 10.6213 24.5313 10.8073 24.5019 10.9961C24.4803 11.0394 24.4752 11.087 24.4681 11.1336C24.3191 11.6518 24.0845 12.1505 23.7466 12.5762C23.1812 13.3302 22.3515 13.8551 21.4694 14.183C20.3691 14.5831 19.1656 14.647 18.0098 14.5045C17.3123 14.4177 16.6354 14.202 16.0108 13.8861C15.9304 13.8484 15.8604 13.7892 15.7722 13.7699C15.6631 13.674 15.5326 13.6018 15.4055 13.5316C15.3011 13.42 15.1578 13.3535 15.0503 13.245C14.9463 13.1351 14.8107 13.0575 14.7256 12.9304C14.5147 12.7323 14.3461 12.497 14.1862 12.2587C13.8286 11.6878 13.613 11.0327 13.5661 10.3636C13.5738 10.0644 13.5411 9.7618 13.5972 9.46621C13.6049 9.30777 13.6516 9.15465 13.6759 8.99853C13.7563 8.74955 13.8317 8.49823 13.9449 8.26123C14.2001 7.71432 14.5863 7.23232 15.0439 6.83621C15.2558 6.67677 15.4549 6.49602 15.6955 6.37851C16.0118 6.15649 16.3785 6.02134 16.7367 5.88054C17.3822 5.64853 18.0683 5.52903 18.7536 5.51871ZM18.5235 7.48198C17.7928 7.57019 17.0703 7.8731 16.5667 8.41301C16.2034 8.81213 15.9628 9.3304 15.9405 9.86899C15.8885 10.4206 16.0568 10.9828 16.3819 11.4328C16.5039 11.6092 16.6688 11.75 16.8222 11.8988C17.5782 12.51 18.6087 12.6977 19.562 12.5935C20.3846 12.5126 21.2187 12.1598 21.728 11.4987C22.2092 10.8892 22.3653 10.0434 22.1163 9.30943C21.9649 8.8038 21.6181 8.3724 21.1913 8.06184C20.706 7.72064 20.1234 7.52292 19.5306 7.47332C19.3271 7.47832 19.1247 7.43038 18.9219 7.45968C18.7898 7.47665 18.6556 7.46533 18.5235 7.48198Z" fill="#787D89" />
  <path d="M54.3877 5.52807C54.8051 5.49113 55.2241 5.52874 55.6408 5.55737C56.6405 5.65357 57.6134 5.99809 58.4431 6.55531C58.6587 6.71842 58.8706 6.88685 59.0704 7.0686C59.286 7.31326 59.5046 7.55891 59.6733 7.83886C60.0342 8.41838 60.2488 9.08612 60.2856 9.76518C60.287 10.1047 60.3008 10.4472 60.2309 10.7818C60.1042 11.5131 59.7726 12.2081 59.2819 12.7707C58.9487 13.1571 58.5432 13.478 58.1031 13.7403C57.5682 14.0299 57.0028 14.2772 56.4015 14.3971C55.5813 14.5785 54.7304 14.6034 53.896 14.5196C53.1883 14.4433 52.4874 14.2636 51.8517 13.9437C51.2231 13.6508 50.6492 13.2317 50.2129 12.6961C49.7273 12.1199 49.4123 11.4076 49.3052 10.6666C49.216 9.97388 49.267 9.25755 49.5097 8.59813C49.68 8.1401 49.9287 7.71004 50.2488 7.33755C50.3468 7.24435 50.4275 7.1365 50.5195 7.03797C50.8111 6.78832 51.1045 6.53567 51.4441 6.34993C52.0274 6.02272 52.6584 5.76408 53.3214 5.64724C53.6726 5.57667 54.0295 5.53573 54.3877 5.52807ZM54.2762 7.47736C53.5624 7.55492 52.8628 7.84152 52.3421 8.33417C52.1751 8.50926 52.0261 8.70265 51.9156 8.91736C51.5803 9.52118 51.5367 10.2738 51.7973 10.9119C52.0048 11.4428 52.4181 11.8832 52.9172 12.1635C53.6053 12.558 54.4299 12.6718 55.214 12.5969C55.8088 12.5433 56.3999 12.3522 56.8865 12.0064C57.3637 11.6669 57.7263 11.1632 57.8598 10.5954C58.045 9.85339 57.8422 9.02554 57.3221 8.45633C56.9119 7.99298 56.3289 7.69905 55.7283 7.55459C55.2535 7.44141 54.7601 7.4454 54.2762 7.47736Z" fill="#6FABB6" />
  <path d="M18.523 7.48204C18.6551 7.4654 18.7893 7.47672 18.9214 7.45974C19.1242 7.43045 19.3266 7.47838 19.5301 7.47339C20.1228 7.52299 20.7055 7.72071 21.1908 8.0619C21.6176 8.37247 21.9643 8.80387 22.1157 9.3095C22.3648 10.0435 22.2087 10.8893 21.7274 11.4988C21.2181 12.1599 20.3841 12.5127 19.5615 12.5936C18.6081 12.6978 17.5777 12.51 16.8217 11.8989C16.6683 11.7501 16.5034 11.6093 16.3814 11.4329C16.0562 10.9828 15.8879 10.4206 15.94 9.86905C15.9623 9.33047 16.2029 8.81219 16.5662 8.41308C17.0698 7.87317 17.7923 7.57025 18.523 7.48204Z" fill="#F4C04B" />
</svg>;

const FormStyled = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  .title-consent {
    color: #1D2129;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;  
  }

  .consent-accept-title {
    color: #1D2129;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    margin-bottom: 8px;
  }

  .form-content {
    border-right: 1px solid #86909C;
    background: #F6F7FC;
    box-shadow: 4px 0 8px 0 rgba(0, 0, 0, 0.15);
    flex: 1;
    padding: 0 16px;
    padding-top: 24px;
    gap: 24px;
    align-self: stretch;
    zoom: ${ZOOM_SIZE};
    .ant-form-item {
      margin-bottom: 16px;
      .ant-form-item-label {
        color: #1D2129;
        font-family: Poppins;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }

      .ant-input {
        color: #1D2129;
        font-family: Poppins;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
      }
    }
  }

  .date-group {
    display: flex;
    align-items: center;
    gap: 8px;
    span {
      color: #1D2129;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      &.value {
        font-weight: 500;
      }
    }
  }

  .clear-text {
    color: #DA072D;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  .sign-above {
    color: #505050;
    text-align: right;
    font-family: Poppins;
    font-size: 18px;
    font-style: italic;
    font-weight: 400;
    line-height: normal;
  }

  .signature-footer {
    border-top: 1px solid #86909C;
  }

  .footer-action-text {
    color: #505050;
    text-align: center;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .footer-consent {
    display: flex;
    height: 28px;
    padding: 8px 4px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    align-self: stretch;
    background: #F5F5F5;
  }

  .consent-content {
    display: flex;
    padding: 8px 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    align-self: stretch;
    border-radius: 4px;
    border: 1px solid #CCD4DC;
    background: #F6F7FC;
    margin-bottom: 16px;
    max-height: 130px;
    overflow: auto;
    p {
      color: #1D2129;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      align-self: stretch;
    }
  }

  .checkbox-container {
    align-items: flex-start !important;
  }

  .footer-action {
    gap: 16px;
    .btn {
      display: flex;
      height: 48px;
      padding: 0px 24px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      flex: 1 0 0;
      border-radius: 8px;
      background: #E5E6EB;
      span {
        color: #1D2129;
        text-align: center;
        font-family: Poppins;
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
      }

      &.btn-submit {
        background: #2463EB;
        span {
          color: #fff;
        }
      }

      &:disabled {
        opacity: 0.5;
      }
    }
  }
`;
